import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { usePickupBag } from '@/hooks/usePickupBag';
import { cdn } from '@/config/cdn';
import QuantityStepper from './QuantityStepper';

type Props = { open: boolean; onClose: () => void };

const fmt = (n: number) => `$${n.toFixed(2)}`;

/** REF-08: white Pickup Bag drawer. Adding items never submits anything. */
export default function PickupDrawer({ open, onClose }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const { lines, totalQuantity, subtotal, updateQuantity, removeItem } = usePickupBag();

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  const tab = open ? 0 : -1;

  return (
    <div className={`fixed inset-0 z-[70] ${open ? '' : 'pointer-events-none'}`} aria-hidden={!open}>
      <div className={`absolute inset-0 bg-[rgba(23,21,21,0.35)] transition-opacity duration-300 motion-reduce:transition-none ${open ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="pickup-bag-title"
        className={`absolute right-0 top-0 h-full w-full sm:w-[460px] bg-white text-[#171515] flex flex-col transition-transform duration-500 ease-[cubic-bezier(0.2,0.7,0.2,1)] motion-reduce:transition-none ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex justify-end px-6 md:px-8 pt-6">
          <button ref={closeRef} type="button" onClick={onClose} tabIndex={tab} className="fold-label whitespace-nowrap cursor-pointer flex items-center gap-2 py-2">
            Close <span className="w-1.5 h-1.5 rounded-full bg-[#171515]" aria-hidden="true" />
          </button>
        </div>

        <div className="px-6 md:px-8 mt-8 flex items-baseline justify-between border-b border-[rgba(23,21,21,0.12)] pb-6">
          <h2 id="pickup-bag-title" className="text-xl">Your Pickup Bag</h2>
          <span className="fold-label text-[#8A8580] tabular-nums">{totalQuantity} {totalQuantity === 1 ? 'piece' : 'pieces'}</span>
        </div>

        <div className="px-6 md:px-8 flex-1 overflow-y-auto overscroll-contain" aria-live="polite">
          {lines.length === 0 ? (
            <div className="py-16">
              <p className="fold-serif text-2xl leading-tight">Your bag is empty.</p>
              <p className="mt-3 text-sm text-[#5d5853]">Choose a few bakes and they&apos;ll wait here.</p>
              <Link to="/collection" onClick={onClose} tabIndex={tab} className="mt-8 inline-block fold-label fold-link whitespace-nowrap cursor-pointer">Browse the collection</Link>
            </div>
          ) : (
            <ul>
              {lines.map(({ product, quantity }) => (
                <li key={product.slug} className="grid grid-cols-[64px_minmax(0,1fr)_auto] gap-x-4 sm:gap-x-5 py-5 border-b border-[rgba(23,21,21,0.12)]">
                  <div className="w-16 h-16 overflow-hidden bg-[#e4e0da]">
                    <img src={cdn(product.coverImage)} alt={product.name} className="w-full h-full object-cover object-center" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm leading-snug">{product.name}</p>
                    <p className="mt-1 text-[10px] tracking-[0.14em] uppercase text-[#8A8580] tabular-nums">{fmt(product.price)} each</p>
                    <div className="mt-2 -ml-px flex items-center gap-4 flex-wrap">
                      <QuantityStepper value={quantity} onChange={(n) => updateQuantity(product.slug, n)} label={`${product.name} quantity`} />
                      <button type="button" tabIndex={tab} onClick={() => removeItem(product.slug)} className="text-xs text-[#5d5853] underline underline-offset-4 decoration-[rgba(23,21,21,0.25)] hover:text-[#171515] whitespace-nowrap cursor-pointer" aria-label={`Remove ${product.name}`}>
                        Remove
                      </button>
                    </div>
                  </div>
                  <p className="text-sm tabular-nums mt-0.5">{fmt(product.price * quantity)}</p>
                </li>
              ))}
            </ul>
          )}
          <div className="py-6 text-sm leading-relaxed text-[#5d5853] space-y-2">
            <p>Pickup only, at the counter. Requests are placed the day before and collected during opening hours.</p>
            <p className="text-xs text-[#8A8580]">Prices are estimates — nothing is charged online.</p>
          </div>
        </div>

        <div className="px-6 md:px-8 py-5 border-t border-[rgba(23,21,21,0.12)] flex items-baseline justify-between">
          <span className="text-sm">Estimated subtotal</span>
          <span className="text-sm tabular-nums">{fmt(subtotal)}</span>
        </div>
        <div className="grid grid-cols-2 border-t border-[rgba(23,21,21,0.12)]">
          <button type="button" onClick={onClose} tabIndex={tab} className="whitespace-nowrap py-5 text-[14px] cursor-pointer hover:bg-[#F5F1EB] transition-colors">
            Continue browsing
          </button>
          {lines.length > 0 ? (
            <Link to="/visit#pickup" onClick={onClose} tabIndex={tab} className="group whitespace-nowrap bg-[#171515] text-[#F5F1EB] py-5 text-[14px] cursor-pointer flex items-center justify-center gap-2 hover:bg-[#2b2827] transition-colors">
              Review pickup <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
            </Link>
          ) : (
            <span className="whitespace-nowrap bg-[#171515] text-[#F5F1EB] opacity-40 py-5 text-[14px] flex items-center justify-center">Review pickup</span>
          )}
        </div>
      </aside>
    </div>
  );
}